import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import type { ReviewTranslation, ReviewLink, ReviewGuest } from '../../types/review';
import { HOTEL_ID } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';
import type { SupportedLanguages } from '../../i18n';

class ReviewClient {
  private static instance: ReviewClient;
  private sessionId: string;
  private submittedPlatforms: Set<string> = new Set();
  private rateLimitTimestamps: number[] = [];
  private readonly RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
  private readonly RATE_LIMIT_MAX_REQUESTS = 5; // 5 requests per minute

  private constructor() {
    this.sessionId = uuidv4();
  }

  public static getInstance(): ReviewClient {
    if (!ReviewClient.instance) {
      ReviewClient.instance = new ReviewClient();
    }
    return ReviewClient.instance;
  }

  public async getReviewTranslations(sectionId?: string): Promise<ReviewTranslation[]> {
    try {
      let query = supabaseClient
        .from('review_translations')
        .select('*')
        .eq('hotel_id', HOTEL_ID);

      if (sectionId) {
        query = query.eq('section_id', sectionId);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error fetching review translations', {
        category: 'Review',
        data: { error, hotelId: HOTEL_ID, sectionId }
      });
      throw error;
    }
  }

  public async getReviewLinks(sectionId?: string): Promise<ReviewLink[]> {
    try {
      let query = supabaseClient
        .from('review_links')
        .select('*')
        .eq('hotel_id', HOTEL_ID)
        .eq('active', true);

      if (sectionId) {
        query = query.eq('section_id', sectionId);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error fetching review links', {
        category: 'Review',
        data: { error, hotelId: HOTEL_ID, sectionId }
      });
      throw error;
    }
  }

  public async submitReview(review: ReviewGuest): Promise<boolean> {
    try {
      // Check rate limiting
      if (this.isRateLimited()) {
        logger.warn('Rate limit exceeded for review submission', {
          category: 'Review',
          data: { sessionId: this.sessionId }
        });
        return false;
      }

      // Check if already submitted for this platform
      const platformKey = `${review.platform}`;
      if (this.submittedPlatforms.has(platformKey)) {
        logger.warn('Review already submitted for this platform', {
          category: 'Review',
          data: { platform: review.platform, sessionId: this.sessionId }
        });
        return false;
      }

      // Add session ID and current timestamp
      const reviewWithSession: ReviewGuest = {
        ...review,
        session_id: this.sessionId,
        hotel_id: HOTEL_ID
      };

      // Insert the review into the database
      const { data, error } = await supabaseClient
        .from('review_guests')
        .insert([reviewWithSession])
        .select();

      if (error) {
        throw error;
      }

      // Mark this platform as submitted
      this.submittedPlatforms.add(platformKey);
      
      // Update rate limit tracking
      this.rateLimitTimestamps.push(Date.now());

      return true;
    } catch (error) {
      logger.error('Error submitting review', {
        category: 'Review',
        data: { error, platform: review.platform, sessionId: this.sessionId }
      });
      return false;
    }
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    
    // Remove timestamps outside the window
    this.rateLimitTimestamps = this.rateLimitTimestamps.filter(
      timestamp => now - timestamp < this.RATE_LIMIT_WINDOW
    );
    
    // Check if we've exceeded the limit
    return this.rateLimitTimestamps.length >= this.RATE_LIMIT_MAX_REQUESTS;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public resetSession(): void {
    this.sessionId = uuidv4();
    this.submittedPlatforms.clear();
    this.rateLimitTimestamps = [];
  }
}

export const reviewClient = ReviewClient.getInstance();